/**
 * HVP API Client
 *
 * Handles all API communication with server
 * Uses Fetch API with proper error handling and retry logic
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

import { applyMixin } from './mixin.js';
import { ApiEndpointMethods } from './api-endpoints.js';
import { ApiDiagnosticsMethods } from './api-diagnostics.js';

export class HVPApiClient {
    /**
     * Constructor
     *
     * @param {Object} config - Configuration
     * @param {string} config.ajaxUrl - WordPress AJAX URL
     * @param {string} config.nonce - Security nonce
     * @param {boolean} config.debug - Debug mode
     */
    constructor(config) {
        this.ajaxUrl = config.ajaxUrl;
        this.nonce = config.nonce;
        this.debug = config.debug || false;

        // Active requests for cancellation
        this.activeRequests = new Map();

        // Statistics
        this.stats = {
            requests: 0,
            errors: 0,
            retries: 0,
            ajaxRequests: 0
        };
    }

    /**
     * Generic request handler
     *
     * @param {string} action - Action name
     * @param {Object} data - Request data
     * @param {Object} options - Request options
     * @returns {Promise<Object>} Response data
     */
    async request(action, data = {}, options = {}) {
        const requestId = this.generateRequestId();
        const maxRetries = options.maxRetries || 3;
        const timeout = options.timeout || 10000;

        this.stats.requests++;

        try {
            const result = await this.retry(
                () => this.executeRequest(requestId, action, data, timeout),
                maxRetries
            );

            return result;

        } catch (error) {
            this.stats.errors++;
            throw error;

        } finally {
            this.activeRequests.delete(requestId);
        }
    }

    /**
     * Execute single request
     *
     * @private
     */
    async executeRequest(requestId, action, data, timeout) {
        const abortController = new AbortController();
        this.activeRequests.set(requestId, abortController);

        // Set timeout
        const timeoutId = setTimeout(() => abortController.abort(), timeout);

        try {
            // Use WordPress AJAX
            const result = await this.sendAjaxRequest(action, data, abortController.signal);
            this.stats.ajaxRequests++;
            return result;

        } finally {
            clearTimeout(timeoutId);
        }
    }

    /**
     * Send WordPress AJAX request
     *
     * @private
     */
    async sendAjaxRequest(action, data, signal) {
        const start = performance.now();

        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] API Request:', action);
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Request data:', JSON.stringify(data));

        // Build FormData for WordPress AJAX
        const formData = new FormData();
        formData.append('action', action);
        formData.append('nonce', this.nonce);

        // Append data fields
        for (const [key, value] of Object.entries(data)) {
            if (typeof value === 'object') {
                formData.append(key, JSON.stringify(value));
            } else {
                formData.append(key, value);
            }
        }

        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Sending to:', this.ajaxUrl);

        const response = await fetch(this.ajaxUrl, {
            method: 'POST',
            body: formData,
            signal: signal,
            credentials: 'same-origin'
        });

        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Response status:', response.status, response.statusText);

        if (!response.ok) {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ERROR: HTTP error', response.status);
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();

        const elapsed = Math.round(performance.now() - start);
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] API Response received in', elapsed, 'ms');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Response success:', result.success);
        this.log(`AJAX [${action}] completed in ${elapsed}ms`);

        if (!result.success) {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ERROR: Server error:', result.error || result.data?.message);
            throw new Error(result.error || result.data?.message || 'Request failed');
        }

        return result.data;
    }

    /**
     * Retry logic with exponential backoff
     *
     * @param {Function} fn - Function to retry
     * @param {number} maxRetries - Maximum retry attempts
     * @returns {Promise<*>} Function result
     */
    async retry(fn, maxRetries = 3) {
        let lastError;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                return await fn();

            } catch (error) {
                lastError = error;

                // Don't retry if aborted
                if (error.name === 'AbortError') {
                    throw error;
                }

                // Don't retry on client errors (4xx)
                if (error.message.includes('HTTP 4')) {
                    throw error;
                }

                // Last attempt - throw error
                if (attempt === maxRetries) {
                    throw error;
                }

                // Exponential backoff: 100ms, 200ms, 400ms
                const delay = Math.min(100 * Math.pow(2, attempt - 1), 1000);

                this.stats.retries++;
                this.log(`Retry attempt ${attempt}/${maxRetries} after ${delay}ms`);

                await this.sleep(delay);
            }
        }

        throw lastError;
    }

    /**
     * Cancel specific request
     *
     * @param {string} requestId - Request ID
     * @returns {boolean} True if cancelled
     */
    cancel(requestId) {
        if (this.activeRequests.has(requestId)) {
            const controller = this.activeRequests.get(requestId);
            controller.abort();
            this.activeRequests.delete(requestId);
            this.log('Request cancelled:', requestId);
            return true;
        }
        return false;
    }

    /**
     * Cancel all active requests
     *
     * @returns {number} Number of cancelled requests
     */
    cancelAll() {
        let count = 0;

        for (const [requestId, controller] of this.activeRequests.entries()) {
            controller.abort();
            count++;
        }

        this.activeRequests.clear();

        if (count > 0) {
            this.log('Cancelled all requests:', count);
        }

        return count;
    }
}

// Endpoint wrappers + diagnostics live in sibling modules (350-line rule).
applyMixin(HVPApiClient.prototype, ApiEndpointMethods);
applyMixin(HVPApiClient.prototype, ApiDiagnosticsMethods);

export default HVPApiClient;
